<script lang="ts">
    import { _ } from 'svelte-i18n';

    import { debug } from '../../../stores/store';
    import { Assignment, AssignmentPart, AssignmentType } from './AssignmentsData';
    import { RouteInfoUtils } from '../Planning/PageDeliveries2/RouteSummaryApp/RouteInfoUtils';
    import { GanttCouriersSettings } from './GanttCouriersSettings';
    import { FriendlyDateGenerator } from '../../utilities/FriendlyDateGenerator';
    import { smartRounding } from '../telematics/tools/smartRounding';
    import { formatTime } from './formatTime';

    export let assignment: Assignment;
    export let chartStartTime: string;
    export let totalTimelineWidth: number;
    export let ganttCouriersSettings: GanttCouriersSettings;

    // Format time in hours:minutes
    // function formatTime(minutes: number): string {
    //     if (minutes === undefined) return '';
    //     const hours = Math.floor(minutes / 60);
    //     const remainingMinutes = Math.floor(minutes % 60);
    //     return `${hours}:${remainingMinutes.toString().padStart(2, '0')}`;
    // }

    // Calculate offset in minutes from chart start time
    $: timeOffset = (() => {
        if (ganttCouriersSettings.useRealTime) {
            // Real-time mode - calculate offset based on actual start time
            const assignmentStart = new Date(assignment.startTime);
            const chartStart = new Date(chartStartTime);
            const offsetMinutes = Math.floor(
                (assignmentStart.getTime() - chartStart.getTime()) / (60 * 1000)
            );
            // console.log(`Assignment ${assignment.name} offset (real-time):`, {
            //     assignmentStart: assignmentStart.toISOString(),
            //     chartStart: chartStart.toISOString(),
            //     offsetMinutes
            // });
            return offsetMinutes;
        } else {
            // Relativistic mode - all assignments start at 00:00
            // console.log(`Assignment ${assignment.name} offset (relativistic): 0`);
            return 0;
        }
    })();

    // Calculate total time for width scaling
    $: totalTime = assignment.parts.reduce((sum, part) => sum + part.time, 0);

    // Calculate width for each part in pixels
    type PartWithWidth = AssignmentPart & {
        width: number;
        offset: number;
        tooltip: string;
    };

    let partsWithWidth: PartWithWidth[] = [];
    let totalPartsWidth = 0;

    // Get tooltip text based on part type
    function getTooltipText(part: AssignmentPart, index: number): string {
        // Calculate start time for this segment
        const startTimeMinutes = assignment.parts
            .slice(0, index)
            .reduce((sum, p) => sum + p.time, 0);
        const endTimeMinutes = startTimeMinutes + part.time;

        let startTimeStr: string;
        let endTimeStr: string;

        if (ganttCouriersSettings.useRealTime) {
            // Real-time mode - show actual clock time
            const assignmentStart = new Date(assignment.startTime);
            const startTime = new Date(assignmentStart.getTime() + startTimeMinutes * 60000);
            const endTime = new Date(assignmentStart.getTime() + endTimeMinutes * 60000);

            const formatTime = (date: Date) => {
                const hours = date.getHours().toString().padStart(2, '0');
                const minutes = date.getMinutes().toString().padStart(2, '0');
                return `${hours}:${minutes}`;
            };

            startTimeStr = formatTime(startTime);
            endTimeStr = formatTime(endTime);
        } else {
            // Relativistic mode - show duration from 00:00
            startTimeStr = formatTime(startTimeMinutes);
            endTimeStr = formatTime(endTimeMinutes);
        }

        if (part.type === AssignmentType.Delivery) {
            const point = part.pointName || 'Delivery point';
            return (
                $_('gantt-tooltip-delivery', { values: { packages: part.packages } }) +
                `: ${part.time} min, ${point}\n${startTimeStr} - ${endTimeStr}`
            );
        } else {
            const distance = (part.distance / 1000).toFixed(1);
            const from = part.fromAddress || 'Start';
            const to = part.toAddress || 'End';
            const partTime = part.time.toFixed(1);
            return (
                $_('gantt-tooltip-drive', {
                    values: { time: partTime, distance: distance, from: from, to: to },
                }) + `\n${startTimeStr} - ${endTimeStr}`
            );
        }
    }

    // Create parts with width and tooltips - make it reactive to useRealTime changes
    $: {
        totalPartsWidth =
            assignment.parts.reduce(
                (sum, part, index) => sum + part.time + (index === 0 ? timeOffset : 0),
                0
            ) * ganttCouriersSettings.pixelsPerMinute;
        // console.log(
        //     `Recalculating partsWithWidth for ${assignment.name} with useRealTime=${ganttCouriersSettings.useRealTime}`,
        //     totalPartsWidth
        // );
        partsWithWidth = assignment.parts.map((part, index) => {
            const width = part.time * ganttCouriersSettings.pixelsPerMinute;
            const partOffset = index === 0 ? timeOffset * ganttCouriersSettings.pixelsPerMinute : 0;
            const tooltip = getTooltipText(part, index);

            // Create a new object with all properties from the original part
            return {
                type: part.type,
                time: part.time,
                pointName: part.pointName,
                fromAddress: part.fromAddress,
                toAddress: part.toAddress,
                distance: part.distance,
                packages: part.packages,
                routeDeliveryIterator: part.routeDeliveryIterator,
                width: width,
                offset: partOffset,
                tooltip: tooltip,
            } as PartWithWidth;
        });
    }

    $: drivePercentage = Math.round((assignment.totalDriveTime / totalTime) * 100);
    $: deliveryPercentage = Math.round((assignment.totalDeliveryTime / totalTime) * 100);
    $: shortName = assignment.name.substring(0, 3).toUpperCase();
    $: {
        totalTimelineWidth = totalTimelineWidth;
        console.log('totalTimelineWidth, GanttCouriersRow', totalTimelineWidth);
    }
</script>

<tr class="route-row">
    {#if ganttCouriersSettings.visibleDay}
        <td class="col-distance"
            >{new FriendlyDateGenerator(assignment.getDay(), false).getDayAndMonthNumeric()}</td
        >
    {/if}
    {#if ganttCouriersSettings.visibleId || $debug}
        <td class="col-id">
            {#if $debug}
                <div
                    class="route-id-badge"
                    style="background-color: {assignment.color};"
                    title={assignment.name}
                >
                    {assignment.id}
                </div>
            {:else}
                <div
                    class="route-id-badge"
                    style="background-color: {assignment.color};"
                    title={assignment.name}
                >
                    {shortName}
                </div>
            {/if}
        </td>
    {/if}
    {#if ganttCouriersSettings.visibleTime}
        <td class="col-time">{formatTime(totalTime)}</td>
    {/if}
    {#if ganttCouriersSettings.visibleParcels}
        <td class="col-pkg">{assignment.packages}</td>
    {/if}
    {#if ganttCouriersSettings.visibleStops}
        <td class="col-stops">{assignment.stops}</td>
    {/if}
    {#if ganttCouriersSettings.visibleDistance}
        <!--        <td class="col-distance">{(assignment.totalDistance / 1000).toFixed(1)} km</td>-->
        <td class="col-distance" title="{(assignment.totalDistance / 1000).toFixed(1)} km"
            >{new smartRounding().getFormatedValue(assignment.totalDistance / 1000, '', {
                convert_nicer: true,
            })}</td
        >
    {/if}
    {#if ganttCouriersSettings.visibleCost}
        <td
            class="col-distance"
            title="{assignment.cost.toFixed(2)} {RouteInfoUtils.getCurrency()}"
        >
            <!--{assignment.cost.toFixed(0)}-->
            <!--{RouteInfoUtils.getCurrency()}-->
            {new smartRounding().getFormatedValue(assignment.cost, '', {
                convert_nicer: true,
            })}
        </td>
    {/if}
    {#if ganttCouriersSettings.visiblePercents}
        <td class="col-time-of-drive">
            {#if drivePercentage}
                <div class="time-bar" style="position: relative;">
                    <div
                        class="time-bar-drive"
                        style="width: {drivePercentage}%; background-color: {assignment.color}40;"
                    >
                        {drivePercentage}
                    </div>
                    <div
                        class="time-bar-delivery"
                        style="width: {deliveryPercentage}%; background-color: {assignment.color};"
                    ></div>
                    <div
                        style="position: absolute; display: flex; justify-content: flex-end; min-width: 40px; line-height: 20px; right: 4px;"
                    >
                        <span class="time-percentage">{deliveryPercentage}</span>
                        <span class="time-percentage-percent">%</span>
                    </div>
                </div>
            {:else}
                <div class="time-bar">
                    <div
                        class="time-bar-drive"
                        style="width: 100%; background-color: {assignment.color}40;"
                    ></div>
                </div>
            {/if}
        </td>
    {/if}
    {#if false}
        <td class="col-time-of-delivery">
            {#if deliveryPercentage}
                <span class="time-percentage">{deliveryPercentage}</span><span
                    class="time-percentage-percent">%</span
                >
                {#if $debug}
                    ({assignment.dataMode})
                {/if}
            {/if}
        </td>
    {/if}
    {#if ganttCouriersSettings.visibleTimeLine}
        <td class="col-timeline" style="width: 100%;">
            <div class="timeline-container">
                {#if totalTimelineWidth > 0}
                    <div class="timeline-bar" style="width: {totalTimelineWidth}px;">
                        {#each partsWithWidth as part, index}
                            {#if part.offset > 0}
                                <div
                                    class="timeline-segment time-filler"
                                    style="width: {part.offset}px;"
                                ></div>
                            {/if}
                            {#if part.type === AssignmentType.Delivery}
                                <div
                                    class="timeline-segment delivery"
                                    style="width: {part.width}px; background-color: {assignment.color};"
                                    title={part.tooltip}
                                ></div>
                            {:else}
                                <div
                                    class="timeline-segment drive"
                                    style="width: {part.width}px; background-color: {assignment.color}40; border-bottom: 2px solid {assignment.color};"
                                    title={part.tooltip}
                                ></div>
                            {/if}
                        {/each}
                        <div
                            class="timeline-segment time-filler"
                            style="width: {totalTimelineWidth - totalPartsWidth}px;"
                        ></div>
                    </div>
                {:else if $debug}
                    <span>totalTimelineWidth=0</span>
                {/if}
            </div>
        </td>
    {/if}
</tr>

<style>
    .route-row {
        background-color: #fff;
        font-size: 14px;
    }

    .route-row td {
        text-align: right;
        /*padding: 2px 10px;*/
        padding: 1px 3px;
        border-bottom: 1px solid #eee;
    }

    .route-row td:nth-child(7) {
        border-bottom: 0;
    }

    .route-row td:nth-child(8) {
        border-bottom: 0;
    }

    .route-row td:nth-child(9) {
        border-bottom: 0;
    }

    .route-row td.col-id {
        text-align: center;
    }

    .route-id-badge {
        border-radius: 4px;
        color: white;
        padding: 2px;
        font-weight: bold;
        font-size: 12px;
    }

    td.col-time {
        font-weight: bold;
    }

    .time-bar {
        display: flex;
        height: 18px;
        border-radius: 4px;
        overflow: hidden;
        font-size: 10px;
        color: rgba(0, 0, 0, 0.7);
        line-height: 18px;
        text-indent: 4px;
    }

    .time-bar-drive {
        width: 120px;
        height: 100%;
        text-align: left;
        color: #5b5b5b;
        font-size: 14px;
        padding-left: 2px;
        padding-top: 1px;
        z-index: 1;
    }

    .time-bar-delivery {
        margin-left: 1px;
        width: 50px;
    }

    .time-percentage {
        font-weight: bold;
        font-size: 14px;
        text-align: right;
    }

    .time-percentage-percent {
        font-weight: normal;
        font-size: 12px;
    }

    .col-time-of-delivery {
        padding: 0;
        text-align: left;
    }

    .route-row td.col-timeline {
        padding-right: 0;
    }

    .timeline-container {
        margin-left: 12px;
    }

    .timeline-bar {
        display: flex;
        align-items: flex-end;
        height: 18px;
    }

    .timeline-segment {
        height: 18px;
    }

    .timeline-segment.time-filler {
        /*background-color: #f5f5f5;*/
    }
</style>
